import React, { useState, useEffect } from 'react';
import { incidenciasAPI, obrasAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { formatDate, getStatusColor, getGravedadColor, translateEstado, getTodayISO } from '../lib/utils';

const IncidenciasPage = () => {
  const [incidencias, setIncidencias] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterObra, setFilterObra] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIncidencia, setSelectedIncidencia] = useState(null);
  const [formData, setFormData] = useState({
    obra_id: '',
    titulo: '',
    descripcion: '',
    gravedad: 'media',
    estado: 'abierta',
    resolucion: '',
    fecha_resolucion: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [incidenciasRes, obrasRes] = await Promise.all([
        incidenciasAPI.list(),
        obrasAPI.list(),
      ]);
      setIncidencias(incidenciasRes.data);
      setObras(obrasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (dataToSend.estado === 'resuelta' && !dataToSend.fecha_resolucion) {
        dataToSend.fecha_resolucion = getTodayISO();
      }
      
      if (selectedIncidencia) {
        await incidenciasAPI.update(selectedIncidencia.id, dataToSend);
        toast.success('Incidencia actualizada');
      } else {
        await incidenciasAPI.create(dataToSend);
        toast.success('Incidencia registrada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar incidencia');
    }
  };

  const handleEdit = (incidencia) => {
    setSelectedIncidencia(incidencia);
    setFormData({
      obra_id: incidencia.obra_id || '',
      titulo: incidencia.titulo || '',
      descripcion: incidencia.descripcion || '',
      gravedad: incidencia.gravedad || 'media',
      estado: incidencia.estado || 'abierta',
      resolucion: incidencia.resolucion || '',
      fecha_resolucion: incidencia.fecha_resolucion || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await incidenciasAPI.delete(selectedIncidencia.id);
      toast.success('Incidencia eliminada');
      setDeleteDialogOpen(false);
      setSelectedIncidencia(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar incidencia');
    }
  };

  const resetForm = () => {
    setSelectedIncidencia(null);
    setFormData({
      obra_id: '',
      titulo: '',
      descripcion: '',
      gravedad: 'media',
      estado: 'abierta',
      resolucion: '',
      fecha_resolucion: '',
    });
  };

  const getObraName = (obraId) => {
    const obra = obras.find((o) => o.id === obraId);
    return obra ? `${obra.numero} - ${obra.titulo}` : 'Sin obra';
  };

  const translateGravedad = (gravedad) => {
    const map = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };
    return map[gravedad] || gravedad;
  };

  const filteredIncidencias = incidencias.filter((i) => {
    const matchSearch = i.titulo?.toLowerCase().includes(search.toLowerCase()) ||
      i.descripcion?.toLowerCase().includes(search.toLowerCase());
    const matchObra = !filterObra || i.obra_id === filterObra;
    return matchSearch && matchObra;
  });

  const abiertas = incidencias.filter(i => i.estado !== 'resuelta').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidencias-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Incidencias</h1>
          <p className="text-slate-500 text-sm mt-1">{abiertas} incidencias abiertas</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="new-incidencia-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Incidencia
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por título o descripción..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-incidencias-input"
          />
        </div>
        <Select value={filterObra || "all"} onValueChange={(v) => setFilterObra(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[250px]" data-testid="filter-obra-select">
            <SelectValue placeholder="Filtrar por obra" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las obras</SelectItem>
            {obras.map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.numero} - {o.titulo}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cards Grid */}
      {filteredIncidencias.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">{search || filterObra ? 'No se encontraron incidencias' : 'No hay incidencias'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredIncidencias.map((incidencia) => (
            <Card
              key={incidencia.id}
              className={`border-slate-200 hover:border-slate-300 transition-colors ${
                incidencia.gravedad === 'critica' && incidencia.estado !== 'resuelta' ? 'border-l-4 border-l-red-500' : ''
              }`}
              data-testid={`incidencia-card-${incidencia.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1">{incidencia.titulo}</h3>
                    <p className="text-xs text-slate-500">{getObraName(incidencia.obra_id)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleEdit(incidencia)}
                      data-testid={`edit-incidencia-${incidencia.id}`}
                    >
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => { setSelectedIncidencia(incidencia); setDeleteDialogOpen(true); }}
                      data-testid={`delete-incidencia-${incidencia.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-slate-500" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{incidencia.descripcion}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getGravedadColor(incidencia.gravedad)}`}>
                    {translateGravedad(incidencia.gravedad)}
                  </span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getStatusColor(incidencia.estado)}`}>
                    {translateEstado(incidencia.estado)}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-3">{formatDate(incidencia.created_at)}</p>
                {incidencia.resolucion && incidencia.estado === 'resuelta' && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500 font-medium mb-1">Resolución:</p>
                    <p className="text-sm text-slate-600">{incidencia.resolucion}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="incidencia-dialog">
          <DialogHeader>
            <DialogTitle>{selectedIncidencia ? 'Editar Incidencia' : 'Nueva Incidencia'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Obra *</Label>
              <Select
                value={formData.obra_id}
                onValueChange={(value) => setFormData({ ...formData, obra_id: value })}
                required
              >
                <SelectTrigger data-testid="incidencia-obra-select">
                  <SelectValue placeholder="Seleccionar obra" />
                </SelectTrigger>
                <SelectContent>
                  {obras.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.numero} - {o.titulo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Título de la incidencia"
                required
                data-testid="incidencia-titulo-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Describe el problema..."
                rows={3}
                required
                data-testid="incidencia-descripcion-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gravedad</Label>
                <Select
                  value={formData.gravedad}
                  onValueChange={(value) => setFormData({ ...formData, gravedad: value })}
                >
                  <SelectTrigger data-testid="incidencia-gravedad-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="critica">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={formData.estado}
                  onValueChange={(value) => setFormData({ ...formData, estado: value })}
                >
                  <SelectTrigger data-testid="incidencia-estado-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="abierta">Abierta</SelectItem>
                    <SelectItem value="en_proceso">En Proceso</SelectItem>
                    <SelectItem value="resuelta">Resuelta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(formData.estado === 'resuelta' || selectedIncidencia?.estado === 'resuelta') && (
              <div className="space-y-2">
                <Label>Resolución</Label>
                <Textarea
                  value={formData.resolucion}
                  onChange={(e) => setFormData({ ...formData, resolucion: e.target.value })}
                  placeholder="¿Cómo se resolvió?"
                  rows={2}
                  data-testid="incidencia-resolucion-input"
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-incidencia-btn">
                {selectedIncidencia ? 'Guardar' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar incidencia?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la incidencia "{selectedIncidencia?.titulo}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-incidencia-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IncidenciasPage;
